// Página AdminSecurity - Gestión de Roles y Permisos
// Solo accesible por superadmin

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  getAllRoles, 
  createRole, 
  updateRole, 
  deleteRole,
  roleNameExists
} from '../../services/roles.service'
import { MODULE_PERMISSIONS, ACTIONS, getAllModules } from '../../constants/permissions'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import ConfirmDialog from '../../components/ConfirmDialog'
import './AdminSecurity.css'

/**
 * Página AdminSecurity
 * Gestión completa de roles personalizados y permisos
 */
function AdminSecurity() {
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notification, setNotification] = useState(null)
  
  // Estados para formulario de rol
  const [showForm, setShowForm] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {},
    active: true
  })
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  
  // Estados para modales
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // Cargar roles
  useEffect(() => {
    fetchRoles()
  }, [])

  const fetchRoles = async () => {
    try {
      setLoading(true)
      setError(null)
      const rolesData = await getAllRoles()
      setRoles(rolesData)
    } catch (err) {
      console.error('Error cargando roles:', err)
      setError('Error al cargar los roles')
    } finally {
      setLoading(false)
    }
  }

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Manejar cambios en permisos
  const handlePermissionChange = (module, action, checked) => {
    setFormData(prev => {
      const newPermissions = { ...prev.permissions }
      
      if (checked) {
        // Agregar permiso
        if (!newPermissions[module]) {
          newPermissions[module] = []
        }
        if (!newPermissions[module].includes(action)) {
          newPermissions[module].push(action)
        }
      } else {
        // Remover permiso
        if (newPermissions[module]) {
          newPermissions[module] = newPermissions[module].filter(a => a !== action)
          // Si no quedan permisos en el módulo, eliminar el módulo
          if (newPermissions[module].length === 0) {
            delete newPermissions[module]
          }
        }
      }
      
      return {
        ...prev,
        permissions: newPermissions
      }
    })
  }

  // Manejar "Seleccionar todo" de un módulo
  const handleSelectAllModule = (module, checked) => {
    const moduleInfo = MODULE_PERMISSIONS[module]
    if (!moduleInfo) return

    setFormData(prev => {
      const newPermissions = { ...prev.permissions }
      
      if (checked) {
        // Agregar todas las acciones del módulo
        newPermissions[module] = [...moduleInfo.actions]
      } else {
        // Remover todas las acciones del módulo
        delete newPermissions[module]
      }
      
      return {
        ...prev,
        permissions: newPermissions
      }
    })
  }

  // Validar formulario
  const validate = async () => {
    const errors = {}
    
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'El nombre del rol es requerido'
    } else {
      try {
        // Verificar si el nombre ya existe
        const exists = await roleNameExists(formData.name.trim(), editingRole?.id)
        if (exists) {
          errors.name = 'Ya existe un rol con este nombre'
        }
      } catch (error) {
        console.error('Error validando nombre de rol:', error)
        // No bloquear la creación si hay error en la validación del nombre
      }
    }
    
    setFormErrors(errors)
    const isValid = Object.keys(errors).length === 0
    
    if (!isValid) {
      // Mostrar notificación si hay errores
      setNotification({
        type: 'error',
        message: 'Por favor, corrige los errores en el formulario'
      })
      setTimeout(() => setNotification(null), 3000)
    }
    
    return isValid
  }

  // Guardar rol (crear o actualizar)
  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    console.log('=== handleSubmit INICIADO ===')
    console.log('formData:', formData)
    console.log('editingRole:', editingRole)
    console.log('saving:', saving)
    
    // Validar antes de continuar
    console.log('Iniciando validación...')
    const isValid = await validate()
    console.log('Resultado de validación:', isValid)
    console.log('formErrors:', formErrors)
    
    if (!isValid) {
      console.warn('❌ Validación fallida. No se puede continuar.')
      console.log('Errores encontrados:', formErrors)
      // El error ya se muestra en la notificación dentro de validate()
      return
    }

    console.log('✅ Validación exitosa. Iniciando guardado...')
    
    try {
      setSaving(true)
      console.log('Estado saving cambiado a true')
      
      // Preparar datos para guardar
      const roleDataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: formData.permissions,
        active: formData.active
      }
      
      console.log('Datos a guardar:', roleDataToSave)
      
      if (editingRole) {
        console.log('Actualizando rol existente:', editingRole.id)
        await updateRole(editingRole.id, roleDataToSave)
        console.log('✅ Rol actualizado exitosamente')
        setNotification({
          type: 'success',
          message: 'Rol actualizado correctamente'
        })
      } else {
        console.log('Creando nuevo rol...')
        const newRole = await createRole(roleDataToSave)
        console.log('✅ Rol creado exitosamente:', newRole)
        setNotification({
          type: 'success',
          message: 'Rol creado correctamente'
        })
      }
      
      console.log('Reseteando formulario y recargando roles...')
      resetForm()
      await fetchRoles()
      console.log('✅ Proceso completado exitosamente')
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('❌ ERROR al guardar rol:', error)
      console.error('Detalles del error:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      })
      setNotification({
        type: 'error',
        message: `Error: ${error.message || 'No se pudo guardar el rol. Revisa la consola para más detalles.'}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
      console.log('Estado saving cambiado a false')
      console.log('=== handleSubmit FINALIZADO ===')
    }
  }

  // Editar rol
  const handleEdit = (role) => {
    setEditingRole(role)
    setFormData({
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || {},
      active: role.active !== false
    })
    setShowForm(true)
  }

  // Eliminar rol
  const handleDelete = async (roleId) => {
    try {
      await deleteRole(roleId)
      setNotification({
        type: 'success',
        message: 'Rol eliminado correctamente'
      })
      setDeleteConfirm(null)
      await fetchRoles()
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error eliminando rol:', error)
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: {},
      active: true
    })
    setEditingRole(null)
    setShowForm(false)
    setFormErrors({})
  }

  // Verificar si un módulo tiene todos los permisos seleccionados
  const isModuleFullySelected = (module) => {
    const moduleInfo = MODULE_PERMISSIONS[module]
    if (!moduleInfo) return false
    
    const modulePermissions = formData.permissions[module] || []
    return modulePermissions.length === moduleInfo.actions.length
  }

  // Verificar si un permiso está seleccionado
  const isPermissionSelected = (module, action) => {
    return formData.permissions[module]?.includes(action) || false
  }

  if (loading) {
    return (
      <div className="admin-security-page">
        <div className="loading-state">
          <p>Cargando roles...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-security-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchRoles}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-security-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="security-header">
        <div>
          <h1>Gestión de Roles y Permisos</h1>
          <p className="security-subtitle">
            Crea y gestiona roles personalizados con permisos específicos para cada módulo del sistema
          </p>
        </div>
        <div className="header-actions">
          <Link to="/admin">
            <Button variant="outline">← Volver</Button>
          </Link>
          <Button 
            variant="primary" 
            onClick={() => {
              resetForm()
              setShowForm(!showForm)
            }}
          >
            {showForm ? 'Cancelar' : '+ Nuevo Rol'}
          </Button>
        </div>
      </div>

      {/* Formulario de rol */}
      {showForm && (
        <div className="role-form-section">
          <h3>{editingRole ? 'Editar Rol' : 'Nuevo Rol'}</h3>
          <form onSubmit={handleSubmit} className="role-form">
            {/* Información básica */}
            <div className="form-section">
              <h4 className="section-title">Información Básica</h4>
              
              <div className="form-group">
                <label htmlFor="name">
                  Nombre del Rol <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ej: Administrativo, Vendedor, Depósito"
                  className={formErrors.name ? 'error' : ''}
                  disabled={saving}
                  required
                  autoFocus
                />
                {formErrors.name && (
                  <span className="error-message">{formErrors.name}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description">Descripción</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe las responsabilidades de este rol..."
                  rows="3"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <span>Rol activo (los usuarios pueden tener este rol asignado)</span>
                </label>
              </div>
            </div>

            {/* Permisos */}
            <div className="form-section">
              <h4 className="section-title">Permisos</h4>
              <p className="section-description">
                Selecciona los permisos que tendrán los usuarios con este rol. 
                Puedes seleccionar permisos específicos por módulo o seleccionar todo el módulo.
              </p>

              <div className="permissions-container">
                {getAllModules().map((module) => {
                  const moduleInfo = MODULE_PERMISSIONS[module.key]
                  const isFullySelected = isModuleFullySelected(module.key)
                  
                  return (
                    <div key={module.key} className="permission-module">
                      <div className="module-header">
                        <label className="module-checkbox">
                          <input
                            type="checkbox"
                            checked={isFullySelected}
                            onChange={(e) => handleSelectAllModule(module.key, e.target.checked)}
                            disabled={saving}
                          />
                          <span className="module-name">{module.label}</span>
                        </label>
                        <span className="module-description">{module.description}</span>
                      </div>
                      
                      <div className="module-actions">
                        {moduleInfo.actions.map((action) => {
                          const actionLabel = {
                            [ACTIONS.VIEW]: 'Ver',
                            [ACTIONS.CREATE]: 'Crear',
                            [ACTIONS.EDIT]: 'Editar',
                            [ACTIONS.DELETE]: 'Eliminar',
                            [ACTIONS.MANAGE]: 'Gestionar (todo)'
                          }[action] || action
                          
                          return (
                            <label key={action} className="action-checkbox">
                              <input
                                type="checkbox"
                                checked={isPermissionSelected(module.key, action)}
                                onChange={(e) => handlePermissionChange(module.key, action, e.target.checked)}
                                disabled={saving}
                              />
                              <span>{actionLabel}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="form-actions">
              <Button 
                type="button" 
                variant="outline" 
                onClick={resetForm} 
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={saving}
              >
                {saving 
                  ? (editingRole ? 'Guardando...' : 'Creando...') 
                  : (editingRole ? 'Guardar Cambios' : 'Crear Rol')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de roles */}
      <div className="roles-list">
        <h3>Roles Personalizados ({roles.length})</h3>
        {roles.length === 0 ? (
          <div className="empty-state">
            <p>No hay roles personalizados creados. Crea el primero para comenzar.</p>
          </div>
        ) : (
          <div className="roles-grid">
            {roles.map((role) => {
              const modulesCount = Object.keys(role.permissions || {}).length
              const totalPermissions = Object.values(role.permissions || {})
                .reduce((sum, actions) => sum + actions.length, 0)
              
              return (
                <div key={role.id} className={`role-card ${!role.active ? 'inactive' : ''}`}>
                  <div className="role-card-header">
                    <div>
                      <h4 className="role-name">{role.name}</h4>
                      {role.description && (
                        <p className="role-description">{role.description}</p>
                      )}
                    </div>
                    <span className={`status-badge ${role.active ? 'active' : 'inactive'}`}>
                      {role.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  
                  <div className="role-card-body">
                    <div className="role-stats">
                      <div className="stat-item">
                        <span className="stat-value">{modulesCount}</span>
                        <span className="stat-label">Módulos</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{totalPermissions}</span>
                        <span className="stat-label">Permisos</span>
                      </div>
                    </div>
                    
                    {modulesCount > 0 && (
                      <div className="role-permissions-preview">
                        <strong>Permisos:</strong>
                        <div className="permissions-tags">
                          {Object.keys(role.permissions).slice(0, 3).map(module => {
                            const moduleInfo = MODULE_PERMISSIONS[module]
                            return (
                              <span key={module} className="permission-tag">
                                {moduleInfo?.label || module}
                              </span>
                            )
                          })}
                          {modulesCount > 3 && (
                            <span className="permission-tag more">
                              +{modulesCount - 3} más
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="role-card-actions">
                    <button
                      onClick={() => handleEdit(role)}
                      className="action-btn edit-btn"
                      title="Editar"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(role.id)}
                      className="action-btn delete-btn"
                      title="Eliminar"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="Eliminar Rol"
        message="¿Estás seguro de que deseas eliminar este rol? Los usuarios con este rol perderán sus permisos personalizados."
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={() => handleDelete(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
      />
    </div>
  )
}

export default AdminSecurity

